import { useEffect, useState } from "react";
import { Loader2, Copy, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", patch);
      setSettings({ ...settings, ...data });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <Skeleton className="h-64 bg-white/5" />;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-settings-page">
      <div>
        <p className="section-label">Configuration</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight mt-1">System Settings</h2>
      </div>

      <div className="card-surface p-6 flex items-center justify-between gap-6" data-testid="demo-data-setting">
        <div>
          <h3 className="font-display font-semibold text-white">Live Event Engine</h3>
          <p className="text-sm text-slate-400 mt-1">
            Generates realistic live campaign events so dashboards stay visibly live while mobile SDK and partner event streams are connected. Turn off to stop the engine.
          </p>
          <span className={`inline-block mt-2 text-[10px] font-bold tracking-widest px-2 py-1 rounded border ${settings.demo_data_enabled ? "bg-cyan-500/10 text-cyan-300 border-cyan-500/30" : "bg-slate-500/10 text-slate-400 border-slate-500/30"}`}>
            {settings.demo_data_enabled ? "ENGINE ON" : "ENGINE OFF"}
          </span>
        </div>
        <Switch checked={settings.demo_data_enabled} onCheckedChange={(v) => save({ demo_data_enabled: v })} data-testid="demo-data-toggle" />
      </div>

      <div className="card-surface p-6 space-y-5">
        <h3 className="font-display font-semibold text-white">Geofence Defaults</h3>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-slate-300">Default Radius (m)</Label>
            <Input type="number" min={100} max={settings.max_radius_m} value={settings.default_radius_m}
              onChange={(e) => setSettings({ ...settings, default_radius_m: Number(e.target.value) })}
              className="mt-1.5 bg-secondary/50 border-white/10" data-testid="default-radius-input" />
          </div>
          <div>
            <Label className="text-slate-300">Maximum Radius (m)</Label>
            <Input type="number" min={100} max={5000} value={settings.max_radius_m}
              onChange={(e) => setSettings({ ...settings, max_radius_m: Number(e.target.value) })}
              className="mt-1.5 bg-secondary/50 border-white/10" data-testid="max-radius-input" />
          </div>
          <div>
            <Label className="text-slate-300">Default Timezone</Label>
            <Input value={settings.timezone} onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
              className="mt-1.5 bg-secondary/50 border-white/10" data-testid="timezone-input" />
          </div>
          <div>
            <Label className="text-slate-300">Data Retention (days)</Label>
            <Input type="number" min={1} value={settings.retention_days}
              onChange={(e) => setSettings({ ...settings, retention_days: Number(e.target.value) })}
              className="mt-1.5 bg-secondary/50 border-white/10" data-testid="retention-input" />
          </div>
        </div>
        <Button onClick={() => save({
          default_radius_m: settings.default_radius_m,
          max_radius_m: settings.max_radius_m,
          timezone: settings.timezone,
          retention_days: settings.retention_days,
        })} disabled={saving} className="bg-blue-600 hover:bg-blue-500" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Settings"}
        </Button>
      </div>

      <div className="card-surface p-6 space-y-4">
        <h3 className="font-display font-semibold text-white">API Settings</h3>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-slate-400">Google Maps Platform:</span>
          {settings.google_maps_configured ? (
            <span className="flex items-center gap-1.5 text-emerald-400"><CheckCircle2 className="w-4 h-4" /> Configured</span>
          ) : (
            <span className="flex items-center gap-1.5 text-amber-400" data-testid="maps-not-configured">
              <XCircle className="w-4 h-4" /> Not configured — using OpenStreetMap dark tiles with Nominatim search. Set GOOGLE_MAPS_API_KEY to enable Google Maps.
            </span>
          )}
        </div>
        <div>
          <Label className="text-slate-300">Event Ingestion API Key (for mobile SDK / partner event streams)</Label>
          <div className="flex gap-2 mt-1.5">
            <Input readOnly value={settings.api_key || ""} className="bg-secondary/50 border-white/10 font-mono text-xs" data-testid="api-key-display" />
            <Button variant="outline" className="border-white/10 shrink-0" onClick={() => { navigator.clipboard.writeText(settings.api_key || ""); toast.success("API key copied"); }} data-testid="copy-api-key">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            POST events to /api/events/impression, /api/events/engagement, /api/events/click or /api/events/geofence with header X-API-Key.
          </p>
        </div>
      </div>
    </div>
  );
}
